'use client'

import { VideoPlayer } from '@components/common/VideoPlayer'
import useModal from '@components/layout/Modal'
import UIFlex from '@components/ui/UIFlex'
import UIImage from '@components/ui/UIImage'
import useMobileDisabled from '@hooks/useMobileDisabled'
import { Box } from '@mui/joy'
import { VodInfo } from '@schemas/non-auth'
import { PlayingVideoState, playingVideoState } from '@store/playingVideo'
import { pxToRem } from '@ui/style/muiTheme'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { useIsAppControl, appInterface } from '@store/isApp'
import { useTTSControl } from '@store/tts'
import IconArticleVideoPlay from 'src/assets/icons/IconArticleVideoPlay'
import { useNotification } from '@lib/notification'

interface VideoBoxProps {
	videoInfo?: VodInfo
	articleIdx?: string
}

const VideoBox = ({ videoInfo, articleIdx }: VideoBoxProps) => {
	const pathname = usePathname()
	const nativeEvent = useRecoilValue(appInterface)
	const [vodController, setVodController] = useState<JTBCVideoPlayerWrapper>()
	const [enablePIP, setEnablePIP] = useState<boolean>(false)
	const isVertical = videoInfo?.videoType.code === 'SHORT_FORM'
	const [playingVideo, setPlayingVideo] = useRecoilState<PlayingVideoState>(playingVideoState)

	const mobileDisabled = useMobileDisabled()
	const { netWork: netWorkConfirm } = useModal()
	const { isApp } = useIsAppControl()
	const { isTTSPlaying, setIsTTSPlaying } = useTTSControl()
	const { notify } = useNotification()

	const isPlay = useMemo(() => {
		return videoInfo?.videoIdx === playingVideo.videoIdx
	}, [videoInfo?.videoIdx, playingVideo])

	const VodPlayer = useMemo(() => {
		const isPreview = pathname.includes('/preview')
		return (
			<Box
				sx={{
					'& .vod-container': {
						height: '100%',
					},
				}}
			>
				<VideoPlayer
					isAutoPlay
					isPlayAd={!isPreview}
					videoIdx={videoInfo?.videoIdx ?? ''}
					ratio={isVertical ? '9/16' : '16/9'}
					src={{
						video: `${process.env.NEXT_PUBLIC_JTBC_API}/v2/news/${videoInfo?.videoIdx}${
							articleIdx ? `?articleIdx=${articleIdx}` : ''
						}`,
						vmap: !isVertical
							? `${process.env.NEXT_PUBLIC_JTBC_API}/ad/vmap/${videoInfo?.videoIdx}`
							: undefined,
					}}
					type={isVertical ? 'VERTICAL_NEWS_ARTICLE' : 'VOD_NEWS'}
					themeColor={{
						primary: 'rgba(255, 13, 71, 1)',
						secondary: 'rgba(115, 115, 115, 1)',
					}}
					setController={setVodController}
					isMuted={false}
				/>
			</Box>
		)
	}, [pathname, videoInfo?.videoIdx, isVertical, articleIdx])

	const playVideoEvent = useCallback(() => {
		nativeEvent({
			key: 'requestDeviceIsMobile',
			value: null,
		})
		if (mobileDisabled) {
			netWorkConfirm()
		} else if (videoInfo?.videoIdx) {
			setPlayingVideo({ videoIdx: videoInfo.videoIdx })
		}

		if (isApp && isTTSPlaying) {
			setIsTTSPlaying(false)
			notify('본문 듣기를 종료 하였습니다.')
		}
	}, [
		mobileDisabled,
		nativeEvent,
		netWorkConfirm,
		setPlayingVideo,
		videoInfo,
		isApp,
		notify,
		setIsTTSPlaying,
		isTTSPlaying,
	])

	const handlePIP = useCallback(() => {
		if (vodController) {
			const isPIPMode = !!vodController?.container?.querySelector('.pip-mode')
			if (videoInfo?.videoIdx === playingVideo.videoIdx) {
				const vodPosition = vodController?.container.getBoundingClientRect()
				const vodClientHeight = vodController?.container?.clientHeight ?? 0

				/** 같은 router 를 렌딩할때 발생할수없는 상황(if 조건문)이 발생함.. */
				if (vodClientHeight === 0 && (vodPosition?.top ?? 0) > 0) {
					if (isPIPMode) {
						vodController?.onTogglePIP()
					}
					setEnablePIP(false)
					return
				}

				if (
					vodClientHeight + (vodPosition?.top ?? 0) < 0 ||
					vodPosition.top > window.innerHeight
				) {
					if (!enablePIP && !isPIPMode) {
						vodController?.onTogglePIP()
						setEnablePIP(true)
					}
				} else if (enablePIP) {
					if (isPIPMode) {
						vodController?.onTogglePIP()
					}
					setEnablePIP(false)
				}
			}
		}
	}, [vodController, enablePIP, videoInfo, playingVideo])

	useEffect(() => {
		if (vodController && videoInfo?.videoIdx !== playingVideo.videoIdx) {
			const isPIPMode = !!vodController?.container?.querySelector('.pip-mode')
			setEnablePIP(false)
			if (isPIPMode) {
				vodController?.onTogglePIP()
			}
		}
	}, [vodController, videoInfo, playingVideo])

	useEffect(() => {
		window.addEventListener('scroll', handlePIP)
		return () => {
			window.removeEventListener('scroll', handlePIP)
		}
	}, [vodController, handlePIP])

	const isShorts = videoInfo?.videoType.code === 'SHORT_FORM'
	return (
		<UIFlex alignItems="center" width="100%" className="videoPlayerContainer">
			<UIFlex
				width={
					isVertical
						? { xs: '56.09%', md: pxToRem(320), lg: pxToRem(360), xl: pxToRem(400) }
						: '100%'
				}
				sx={{
					'& .advertisement-container > .button-wrap': {
						'& > button:nth-last-of-type(2)': {
							display: 'none',
						},
					},
					'& .function-container > .right': {
						'& > button:nth-of-type(2):nth-last-of-type(4)': {
							display: 'none',
						},
						'& > button:nth-of-type(4):nth-last-of-type(2)': {
							display: 'none',
						},
					},
				}}
			>
				{isPlay ? (
					VodPlayer
				) : (
					<Box position="relative">
						<UIImage
							ratio={isShorts ? '9/16' : '16/9'}
							src={videoInfo?.vodThumbnailImgUrl}
							alt=" "
							onClick={playVideoEvent}
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'fill',
								backgroundColor: '#000',
								cursor: 'pointer',
							}}
						/>
						<UIFlex
							position="absolute"
							top={0}
							left={0}
							right={0}
							border={0}
							width="100%"
							height="100%"
							alignItems="center"
							justifyContent="center"
							sx={{
								background:
									'linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%)',
								pointerEvents: 'none',
								'& .icon-md': {
									display: {
										xs: 'block',
										md: isShorts ? 'block' : 'none',
									},
								},
								'& .icon-lg': {
									display: {
										xs: 'none',
										md: isShorts ? 'none' : 'block',
									},
								},
							}}
						>
							<IconArticleVideoPlay className="icon-lg" />
							<IconArticleVideoPlay className="icon-md" size="md" />
						</UIFlex>
					</Box>
				)}
			</UIFlex>
		</UIFlex>
	)
}

export default VideoBox
